import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/env';
import AuthShell from '../Layout/AuthShell';

const isCredentialFailure = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return (
    (normalized.includes('password') && normalized.includes('incorrect'))
    || normalized.includes('invalid credential')
    || normalized.includes('no user')
    || normalized.includes('account not found')
  );
};

const getLoginFailureMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) {
    return 'Login failed because we could not reach the service. Please check your connection and try again.';
  }

  const errorData = error.response?.data as
    | { expandedError?: { message?: string }; message?: string }
    | undefined;
  const detailedMessage = (errorData?.expandedError?.message || errorData?.message || '').trim();
  const statusCode = error.response?.status;

  if (detailedMessage.length > 0) {
    if (isCredentialFailure(detailedMessage)) {
      return 'Incorrect email/username or password.';
    }

    return detailedMessage;
  }

  if (statusCode === 401) {
    return 'Incorrect email/username or password.';
  }

  if (statusCode === 429) {
    return 'Too many login attempts. Please wait a minute and try again.';
  }

  if (statusCode === 502 || statusCode === 503 || statusCode === 504) {
    return 'Login is temporarily unavailable because the service is not responding. Please retry in a moment.';
  }

  if (statusCode && statusCode >= 500) {
    return 'The login service is temporarily unavailable. Please retry shortly.';
  }

  if (!error.response || error.code === 'ECONNABORTED' || error.message.toLowerCase().includes('timeout')) {
    return 'Login could not reach the backend right now. This is usually temporary, so please retry in a minute.';
  }

  return 'Login failed. Please try again.';
};

const Login: React.FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (location.state) {
      const state = location.state as { message?: string; prefilledUsername?: string };
      if (state.prefilledUsername) {
        setIdentifier(state.prefilledUsername);
        window.setTimeout(() => {
          passwordRef.current?.focus();
        }, 100);
      }

      setSuccessMessage(state.message || '');
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!identifier.trim() || !password.trim()) {
      setError('Please enter both your email or username and password.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(API_ENDPOINTS.LOGIN, {
        name: identifier.trim(),
        passcode: password,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success === false) {
        const errorMessage = response.data.expandedError?.message || 'Login failed. Please try again.';

        if (isCredentialFailure(errorMessage)) {
          setError('Incorrect email/username or password.');
        } else {
          setError(errorMessage);
        }
        return;
      }

      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      localStorage.setItem('userId', response.data.id);
      localStorage.setItem('username', identifier.trim());
      localStorage.setItem('accessTokenExpiredAt', response.data.accessTokenExpiredAt);
      localStorage.setItem('refreshTokenExpiredAt', response.data.refreshTokenExpiredAt);

      navigate('/dashboard');
    } catch (err) {
      setError(getLoginFailureMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      description="Sign in to monitor live settlement flow, payout status, and operator queues from one workspace."
      footer={(
        <div className="auth-link-row">
          <Link to="/register" className="auth-link">Register new account</Link>
        </div>
      )}
    >
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {successMessage ? <Alert variant="success">{successMessage}</Alert> : null}

      <Form onSubmit={handleSubmit}>
        <Form.Group controlId="username" className="mb-3">
          <Form.Label>Email or username</Form.Label>
          <Form.Control
            className="auth-control"
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="Enter your email or username"
            autoComplete="username"
          />
        </Form.Group>

        <Form.Group controlId="password" className="mb-3">
          <Form.Label>Password</Form.Label>
          <InputGroup className="auth-input-group">
            <Form.Control
              ref={passwordRef}
              className="auth-control"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
            <Button
              type="button"
              variant="outline-secondary"
              className="auth-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? 'Hide' : 'Show'}
            </Button>
          </InputGroup>
          <div className="d-flex justify-content-end mt-2">
            <Link to="/forgot-password" className="auth-link">
              Forgot password?
            </Link>
          </div>
        </Form.Group>

        <div className="d-grid">
          <Button variant="primary" type="submit" className="auth-primary-btn mt-3" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Login'}
          </Button>
        </div>
      </Form>
    </AuthShell>
  );
};

export default Login;
